import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import OfficialAvatar from '@components/OfficialAvatar';
import { Toast } from '@components';
import { useUser } from '@hooks/useUser';
import { useAgency } from '@hooks/useAgency';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import {
  QuickScan,
  downloadQuickScansReport,
  fetchQuickScans,
  fetchQuickScansPdfReportChunk,
} from '../../networkRequest/quickScansService';
import { formatDate } from '../../utils/formatDate';
import Utils from '../../utils/utils';
import ActivityHeaderControls from './components/ActivityHeaderControls';
import ClearancesToolbar from './components/ClearancesToolbar';
import ClearanceDetailSidebar from './components/ClearanceDetailSidebar';
import ExportPdfDialog from './components/ExportPdfDialog';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, activityGridSx, utcFromDayjs } from './components/activityGrid';

/** The tab param value that selects this tab. */
export const QUICK_SCAN_TAB = 'quick_scans';

const ACTION_DISPLAY_NAMES: Record<string, string> = {
  id_scan: 'OCID Scan',
  email_lookup: 'Email Lookup',
  qr_code_scan: 'QR Code Scan',
};

type QuickScanRow = {
  id: number;
  img_url: string;
  first_name: string;
  last_name: string;
  agency: string;
  verified: string;
  action: string;
  identifier: string;
  date: string;
  time: string;
  verifier_img_url: string;
  verifier_name: string;
};

const COLUMNS: GridColDef<QuickScanRow>[] = [
  {
    field: 'first_name',
    headerName: 'First',
    renderCell: (params) => (
      <OfficialAvatar
        img_url={params.row.img_url}
        name={params.row.first_name}
        square={false}
        officialTableView={false}
      />
    ),
    width: 150,
  },
  { field: 'last_name', headerName: 'Last', width: 150 },
  { field: 'agency', headerName: 'Agency', width: 200 },
  {
    field: 'verifier_name',
    headerName: 'Scanned By',
    renderCell: (params) => (
      <OfficialAvatar
        img_url={params.row.verifier_img_url}
        name={params.row.verifier_name}
        square={false}
        officialTableView={false}
      />
    ),
    width: 200,
  },
  { field: 'verified', headerName: 'Verified', width: 100 },
  { field: 'action', headerName: 'Action', width: 150 },
  { field: 'identifier', headerName: 'Identifier', width: 200 },
  { field: 'date', headerName: 'Date', width: 150 },
  { field: 'time', headerName: 'Time', width: 100 },
];

const toRow = (quickScan: QuickScan): QuickScanRow => {
  const { date, time } = formatDate(quickScan.performed_at, 'en-US', true, true);
  return {
    id: quickScan.id,
    img_url: quickScan.agency_official.img_url,
    first_name: quickScan.agency_official.first_name,
    last_name: quickScan.agency_official.last_name,
    agency: quickScan.agency_official.agency.name,
    verified: Utils.formatBoolean(quickScan.official_verified),
    action: ACTION_DISPLAY_NAMES[quickScan.action_type] ?? quickScan.action_type,
    identifier: quickScan.identifier,
    date,
    time,
    verifier_img_url: quickScan.verifier.img_url,
    verifier_name: Utils.fullName(quickScan.verifier.first_name, quickScan.verifier.last_name),
  };
};

// The Quick Scan tab of the Activity page: every quick scan performed by
// someone from the agency
const QuickScanActivityLog: React.FC = () => {
  const { user } = useUser();
  const { agency } = useAgency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [gridPagination, setGridPagination] = useState(() => ({
    page: Utils.parseIntParam(searchParams, 'page') ?? DEFAULT_PAGE,
    pageSize: Utils.parseIntParam(searchParams, 'page_size') ?? DEFAULT_PAGE_SIZE,
  }));
  const [searchText, setSearchText] = useState(() => searchParams.get('search') ?? '');
  const [startDt, setStartDt] = useState<Dayjs | null>(() => Utils.parseDateTimeParam(searchParams, 'start'));
  const [endDt, setEndDt] = useState<Dayjs | null>(() => Utils.parseDateTimeParam(searchParams, 'end'));
  const [quickScans, setQuickScans] = useState<QuickScan[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [selectedQuickScanId, setSelectedQuickScanId] = useState<number | null>(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  const fetchParams = useMemo(
    () => ({
      searchText,
      gridPagination,
      startDt: utcFromDayjs(startDt),
      endDt: utcFromDayjs(endDt),
    }),
    [searchText, gridPagination, startDt, endDt]
  );
  const debouncedParams = useDebouncedValue(fetchParams, 300);
  const rows = useMemo(() => quickScans.map(toRow), [quickScans]);
  // Null when a filter or page change drops the selected quick scan, which closes the sidebar
  const selectedQuickScan = quickScans.find((quickScan) => quickScan.id === selectedQuickScanId) ?? null;

  useEffect(() => {
    const accessToken = user?.accessToken;
    if (!agency?.id || !accessToken) return;
    // Set by the cleanup, so a slow response for an older search is dropped
    let isStale = false;
    const load = async () => {
      setHasError(false);
      setLoading(true);
      const result = await fetchQuickScans(accessToken, agency.id, {
        page: debouncedParams.gridPagination.page,
        pageSize: debouncedParams.gridPagination.pageSize,
        searchText: debouncedParams.searchText,
        startDt: debouncedParams.startDt,
        endDt: debouncedParams.endDt,
      });
      if (isStale) return;
      if (result.data && result.pagination) {
        setQuickScans(result.data);
        setTotalCount(result.pagination.total_count);
      } else {
        console.error('fetchQuickScans error', result.error);
        setHasError(true);
      }
      setLoading(false);
    };
    load();
    return () => {
      isStale = true;
    };
  }, [agency?.id, user?.accessToken, debouncedParams]);

  useEffect(() => {
    const params: Record<string, string> = { tab: QUICK_SCAN_TAB };
    if (debouncedParams.gridPagination.page !== DEFAULT_PAGE) {
      params.page = String(debouncedParams.gridPagination.page);
    }
    if (debouncedParams.gridPagination.pageSize !== DEFAULT_PAGE_SIZE) {
      params.page_size = String(debouncedParams.gridPagination.pageSize);
    }
    if (debouncedParams.searchText) {
      params.search = debouncedParams.searchText;
    }
    if (debouncedParams.startDt) {
      params.start = debouncedParams.startDt;
    }
    if (debouncedParams.endDt) {
      params.end = debouncedParams.endDt;
    }
    setSearchParams(params, { replace: true });
  }, [debouncedParams, setSearchParams]);

  const exportCsv = async () => {
    const accessToken = user?.accessToken;
    if (!agency?.id || !accessToken) return;
    try {
      await downloadQuickScansReport(accessToken, agency.id, {
        searchText: debouncedParams.searchText,
        startDt: debouncedParams.startDt,
        endDt: debouncedParams.endDt,
      });
    } catch (error) {
      console.error('downloadQuickScansReport error', error);
      setHasError(true);
    }
  };

  // Memoized because ExportPdfDialog restarts the download when it changes
  const fetchPdfReportChunk = useCallback(
    (offset: number, limit: number, pageOffset: number) => fetchQuickScansPdfReportChunk(
      user?.accessToken ?? '',
      agency?.id ?? '',
      {
        searchText: debouncedParams.searchText,
        startDt: debouncedParams.startDt,
        endDt: debouncedParams.endDt,
      },
      { offset, limit, pageOffset }
    ),
    [user?.accessToken, agency?.id, debouncedParams.searchText, debouncedParams.startDt, debouncedParams.endDt]
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <ActivityHeaderControls
        startDt={startDt}
        endDt={endDt}
        onStartDtChange={setStartDt}
        onEndDtChange={setEndDt}
        exportItems={[
          { label: 'Export CSV', onClick: exportCsv },
          { label: 'Export PDF', onClick: () => setPdfDialogOpen(true) },
        ]}
      />
      {/* Holds the space of the Checkpoints tab's filter bar, so the grid
          doesn't move when switching tabs. There are no quick scan filters yet. */}
      <Box data-testid='quick-scan-filter-bar-space' sx={{ minHeight: '40px', mb: 2 }} />
      <DataGrid
        rows={rows}
        rowCount={totalCount}
        columns={COLUMNS}
        loading={loading}
        paginationMode='server'
        paginationModel={gridPagination}
        onPaginationModelChange={setGridPagination}
        pageSizeOptions={[10, 25, 50, 100]}
        getRowClassName={(params) => params.row.id === selectedQuickScanId ? 'selected-clearance-row' : ''}
        onRowClick={(params) => setSelectedQuickScanId(params.row.id)}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSorting
        sortingMode='server'
        filterMode='server'
        sx={activityGridSx}
        showToolbar
        slots={{ toolbar: ClearancesToolbar }}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton'
          },
          toolbar: {
            searchText,
            onSearchChange: (value) => {
              setSearchText(value);
              setGridPagination(prev => ({ ...prev, page: 0 }));
            }
          },
        }}
        localeText={{ toolbarColumns: 'Manage Columns' }}
      />
      <ClearanceDetailSidebar
        official={selectedQuickScan?.agency_official ?? null}
        onClose={() => setSelectedQuickScanId(null)}
      />
      <ExportPdfDialog
        open={pdfDialogOpen}
        onClose={() => setPdfDialogOpen(false)}
        totalCount={totalCount}
        fetchPdfReportChunk={fetchPdfReportChunk}
      />
      {hasError && (
        <Toast type='error' onClose={() => setHasError(false)}>
          Something went wrong. Please check your connection and try again.
        </Toast>
      )}
    </Box>
  );
};

export default QuickScanActivityLog;
